import { Injectable } from '@angular/core';

/**
 * Service for handling user avatar display logic (initials and colors)
 * Centralized logic used across multiple components for consistent user avatar display
 */
@Injectable({
  providedIn: 'root',
})
export class UserAvatarService {
  private readonly AVATAR_COLORS = [
    '#ffb897',
    '#a4f8df',
    '#9ce7fc',
    '#ffed91',
    '#bdc6ff',
    '#8efca8',
    '#b0fbe6',
    '#edc196',
    '#87c6fa',
    '#f1aae0',
  ];

  /**
   * Get user initials from first and last name
   * @param firstName User's first name
   * @param lastName User's last name
   * @returns Two-letter uppercase initials (e.g., "JS" for John Smith)
   */
  getInitials(firstName: string | undefined, lastName: string | undefined): string {
    if (!firstName || !lastName) {
      return '';
    }
    return firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
  }

  /**
   * Get consistent background color for user avatar based on a unique identifier
   * @param identifier Unique identifier (email, UUID, etc.)
   * @returns Hex color code
   */
  getAvatarColor(identifier: string | undefined): string {
    if (!identifier) {
      return this.AVATAR_COLORS[0];
    }

    // Generate consistent hash from identifier
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
      hash = identifier.codePointAt(i)! + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % this.AVATAR_COLORS.length;
    return this.AVATAR_COLORS[index];
  }

  /**
   * Get all available avatar colors
   * @returns Array of hex color codes
   */
  getAvailableColors(): string[] {
    return [...this.AVATAR_COLORS];
  }
}
